import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import RecordVoiceOverOutlined from "@mui/icons-material/RecordVoiceOverOutlined";
import UploadArea from "./UploadArea";
import KieVoicePickerModal from "./KieVoicePickerModal";
import { getModelFamily } from "../modelProfiles";

/**
 * Kling 3.0 / O3 voiceover: native AI speech, Kie AI preset voice, or uploaded voice clip.
 */
export default function VoiceoverSection({ form, onChange, lang, modelId }) {
  const family = getModelFamily(modelId || form.model);
  const [voiceModalOpen, setVoiceModalOpen] = React.useState(false);

  if (family !== "kling-3" && family !== "kling-o3") return null;

  const set = (patch) => onChange({ ...form, ...patch });
  const voiceSource = form.voiceSource || "ai";
  const selectedVoiceLabel = form.klingVoiceLabel || "";

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        {lang?.videoStudioVoiceover || "Voiceover"}
      </Typography>
      <Alert severity="info" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          {lang?.videoStudioKlingVoiceHint ||
            "Long videos (>15s): Kling speaks on each clip; after stitching we clone the opening clip’s voice and unify the full track so the same character speaks throughout (lip-sync preserved). Use AI voice for Kling’s image-matched speaker, or pick a Kie AI voice preset."}
        </Typography>
      </Alert>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={voiceSource}
        onChange={(_, v) => {
          if (!v) return;
          set({
            voiceSource: v,
            ...(v !== "upload" ? { referenceAudioUploads: [] } : {}),
            ...(v !== "preset" ? { klingVoicePreset: "", klingVoiceLabel: "" } : {}),
            sound: true,
          });
        }}
        sx={{ flexWrap: "wrap" }}
      >
        <ToggleButton value="ai">
          {lang?.videoStudioVoiceAi || "AI voice"}
        </ToggleButton>
        <ToggleButton value="preset">
          {lang?.videoStudioVoicePreset || "Kie AI voice"}
        </ToggleButton>
        <ToggleButton value="upload">
          {lang?.videoStudioVoiceUpload || "Upload voice"}
        </ToggleButton>
      </ToggleButtonGroup>

      {voiceSource === "preset" && (
        <Box>
          {form.klingVoicePreset ? (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                icon={<RecordVoiceOverOutlined />}
                label={selectedVoiceLabel || form.klingVoicePreset}
                color="primary"
                variant="outlined"
              />
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {lang?.videoStudioVoicePresetRequired || "Choose a Kie AI voice to continue."}
            </Typography>
          )}
          <Button variant="outlined" size="small" onClick={() => setVoiceModalOpen(true)}>
            {form.klingVoicePreset
              ? lang?.videoStudioVoiceChange || "Change voice"
              : lang?.videoStudioVoiceBrowse || "Browse voices…"}
          </Button>
        </Box>
      )}

      {voiceSource === "upload" && (
        <UploadArea
          lang={lang}
          kind="audio"
          accept="audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/x-m4a"
          maxBytes={20 * 1024 * 1024}
          label={lang?.videoStudioVoiceUploadLabel || "Voice clip"}
          hint={
            lang?.videoStudioVoiceUploadHint ||
            "MP3, WAV, or M4A — 5–30 seconds, one speaker, minimal background noise."
          }
          files={form.referenceAudioUploads || []}
          onChange={(referenceAudioUploads) => set({ referenceAudioUploads, sound: true })}
          maxFiles={1}
        />
      )}

      <KieVoicePickerModal
        open={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
        value={form.klingVoicePreset}
        lang={lang}
        onConfirm={(voice) =>
          set({
            klingVoicePreset: voice.id,
            klingVoiceLabel: voice.label,
            voiceSource: "preset",
            sound: true,
          })
        }
      />
    </Stack>
  );
}
